import React, { useEffect, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Butterfly, LivonMark } from "./Brand";

const NavLink = ({ href, children, onClick, testId }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={testId}
    className="text-[12px] tracking-[0.22em] uppercase text-livon-muted hover:text-livon-ink transition-colors duration-300 font-medium"
  >
    {children}
  </a>
);

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-livon-bg/85 backdrop-blur-xl border-b border-livon-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a
          href="#top"
          onClick={scrollTo("top")}
          data-testid="logo-link"
          className="flex items-center gap-3 text-livon-ink"
        >
          <Butterfly size={44} />
          <LivonMark
            className="serif text-2xl tracking-[0.16em]"
            supSize="0.4em"
          />
        </a>

        <nav className="hidden md:flex items-center gap-9">
          <NavLink href="#top" onClick={scrollTo("top")} testId="nav-home">
            {t.nav.home}
          </NavLink>
          <NavLink href="#method" onClick={scrollTo("method")} testId="nav-method">
            {t.nav.method}
          </NavLink>
          <NavLink href="#app" onClick={scrollTo("app")} testId="nav-app">
            {t.nav.app}
          </NavLink>
          <NavLink href="#education" onClick={scrollTo("education")} testId="nav-education">
            {t.nav.education}
          </NavLink>
          <NavLink href="#about" onClick={scrollTo("about")} testId="nav-about">
            {t.nav.about}
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden sm:flex items-center text-[11px] tracking-widest uppercase rounded-full border border-livon-line overflow-hidden bg-white/50"
            data-testid="language-switcher"
          >
            <button
              data-testid="lang-sv"
              onClick={() => setLang("sv")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "sv" ? "bg-livon-ink text-livon-bg" : "text-livon-muted hover:bg-livon-surface"
              }`}
            >
              SV
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en" ? "bg-livon-ink text-livon-bg" : "text-livon-muted hover:bg-livon-surface"
              }`}
            >
              EN
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-livon-line"
            aria-label="Menu"
          >
            <div className="space-y-1.5">
              <span className="block h-px w-5 bg-livon-ink"></span>
              <span className="block h-px w-5 bg-livon-ink"></span>
            </div>
          </button>
        </div>
      </div>

      <div
        className={`md:hidden overflow-hidden transition-[max-height] duration-500 ease-out bg-livon-bg/95 backdrop-blur-xl border-b border-livon-line ${
          open ? "max-h-[80vh]" : "max-h-0"
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-5">
          <NavLink href="#top" onClick={scrollTo("top")} testId="mnav-home">{t.nav.home}</NavLink>
          <NavLink href="#method" onClick={scrollTo("method")} testId="mnav-method">{t.nav.method}</NavLink>
          <NavLink href="#app" onClick={scrollTo("app")} testId="mnav-app">{t.nav.app}</NavLink>
          <NavLink href="#education" onClick={scrollTo("education")} testId="mnav-education">{t.nav.education}</NavLink>
          <NavLink href="#about" onClick={scrollTo("about")} testId="mnav-about">{t.nav.about}</NavLink>
          <div className="flex items-center gap-2 pt-2">
            <button
              data-testid="m-lang-sv"
              onClick={() => setLang("sv")}
              className={`px-3 py-1.5 text-xs tracking-widest uppercase rounded-full border ${
                lang === "sv" ? "bg-livon-ink text-livon-bg border-livon-ink" : "border-livon-line text-livon-muted"
              }`}
            >SV</button>
            <button
              data-testid="m-lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 text-xs tracking-widest uppercase rounded-full border ${
                lang === "en" ? "bg-livon-ink text-livon-bg border-livon-ink" : "border-livon-line text-livon-muted"
              }`}
            >EN</button>
          </div>
        </nav>
      </div>
    </header>
  );
};
